import * as React from "react";
import { X } from "lucide-react";
import { cn } from "./lib/utils";

export interface FilterChipProps {
  /** Filter label */
  label: string;
  /** Filter value */
  value: string;
  /** Remove handler */
  onRemove: () => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * FilterChip - Display active filter with remove button
 *
 * Features:
 * - Shows label and value
 * - Remove button
 * - Keyboard accessible
 * - Blue theme for active filters
 */
const FilterChip = React.forwardRef<HTMLDivElement, FilterChipProps>(
  ({ label, value, onRemove, className }, ref) => {
    const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onRemove();
      }
    };

    return (
      <div
        ref={ref}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-lg",
          "bg-blue-50 border border-blue-200",
          "px-3 py-1.5",
          "text-sm text-blue-900",
          "transition-all",
          className
        )}
      >
        <span className="font-medium">{label}:</span>
        <span>{value}</span>
        <button
          type="button"
          onClick={onRemove}
          onKeyDown={handleKeyDown}
          className="ml-1 text-blue-600 hover:text-blue-800 transition-colors focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded"
          aria-label={`Remove ${label} filter`}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }
);
FilterChip.displayName = "FilterChip";

export { FilterChip };
